import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, LOCALE_ID } from '@angular/core';
import { registerLocaleData, DatePipe } from '@angular/common';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CustomIconService } from './services/custom-icon.service';
import { EnvironmentLoaderService } from './services/environment-loader.service';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ApiModule } from './api/api.module';
import { MaterialModule } from './material/material.module';
import { CreateAftaleComponent } from './components/aftale/create/create-aftale.component';
import { PageNotFoundComponent } from './components/shared/page-not-found/page-not-found.component';
import { AftaleDetailsComponent } from './components/aftale/details/aftale-details.component';
import { LoaderComponent } from './components/shared/loader/loader.component';
import { CredentialsInterceptor } from './interceptors/credentials.interceptor';
import { KundeFormComponent } from './components/aftale/create/forms/kunde-form/kunde-form.component';
import { ProduktFormComponent } from './components/aftale/create/forms/produkt-form/produkt-form.component';
import { AbonnementFormComponent } from './components/aftale/create/forms/abonnement-form/abonnement-form.component';
import { TilvalgFormComponent } from './components/aftale/create/forms/tilvalg-form/tilvalg-form.component';
import { SearchComponent } from './components/shared/search/search.component';
import { SideNavigationComponent } from './components/shared/side-navigation/side-navigation.component';
import { MomentModule } from 'ngx-moment';
import 'moment/locale/da.js';
import { ConfirmDialogComponent } from './components/shared/confirm-dialog/confirm-dialog.component';
import { MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { OrdreListComponent } from './components/ordre/list/ordre-list.component';
import { AnlaegsKomponentComponent } from './components/aftale/create/anlaegs-komponent/anlaegs-komponent.component';
import { AuthService } from './services/auth.service';
import { CreateTilkaldComponent } from './components/ordre/tilkald/create/create-tilkald.component';
import { H401Interceptor } from './interceptors/h401.interceptor';
import { HistoryComponent } from './components/shared/history/history.component';
import { FabrikatInputComponent } from './components/aftale/create/forms/produkt-form/fabrikat-input/fabrikat-input.component';
import { AbonnementComponent } from './components/aftale/details/abonnement/abonnement.component';
import { OrdreComponent } from './components/aftale/details/serviceordre/ordre.component';
import { ForbiddenComponent } from './components/shared/forbidden/forbidden.component';
import { ForbiddenInterceptor } from './interceptors/forbidden.interceptor';
import { EditOrdreComponent } from './components/ordre/edit-ordre/edit-ordre.component';
import { QuickviewComponent } from './components/shared/quickview/quickview.component';
import { AnlaegComponent } from './components/aftale/details/abonnement/anlaeg/anlaeg.component';
import { FooterComponent } from './components/shared/footer/footer.component';
import { AfregningslinieeditaddComponent } from './components/ordre/edit-ordre/afregningslinieeditadd/afregningslinieeditadd.component';
import { PageNavFooterComponent } from './components/pages/page-nav-footer/page-nav-footer.component';
import { PageCleanComponent } from './components/pages/page-clean/page-clean.component';
import { PageCleanWhiteComponent } from './components/pages/page-clean-white/page-clean-white.component';
import { CharacterCountComponent } from './components/shared/character-count/character-count.component';
import { dkCurrencyPipe } from './pipes/dk-currency';
import { dkPhoneNumberPipe } from './pipes/dk-phonenumber';
import localeDA from '@angular/common/locales/da';
import { AnlaegEditComponent } from './components/aftale/details/abonnement/anlaeg/anlaeg-edit/anlaeg-edit.component';
import { FastprisAddEditComponent } from './components/aftale/details/abonnement/fastpris-add-edit/fastpris-add-edit.component';
import { AftaleFilterComponent } from './components/aftale/aftale-filter/aftale-filter.component';
import { AftaleListComponent } from './components/aftale/list/aftale-list.component';
import { CreateEftersynComponent } from './components/ordre/eftersyn/create-eftersyn/create-eftersyn.component';
import { OrdreFilterComponent } from './components/ordre/ordre-filter/ordre-filter.component';
import { DkDateAdapter } from './common/DkDateAdaptor';
import { EditTagsComponent } from './components/aftale/edit-tags/edit-tags.component';
import { RabatAddEditComponent } from './components/aftale/details/abonnement/rabat-add-edit/rabat-add-edit.component';
import { RabatFormComponent } from './components/aftale/create/forms/tilvalg-form/rabat-form/rabat-form.component';
import { PosteringsListComponent } from './components/ordre/edit-ordre/posterings-list/posterings-list.component';
import { UnparentedOrderDetailsComponent } from './components/ordre/list/uparented-orders-list/unparented-order-details/unparented-order-details.component';
import { UparentedOrdersListComponent } from './components/ordre/list/uparented-orders-list/uparented-orders-list.component';
import { NotificationsModule } from './notifications/notifications.module';
import { OpkraevningsIntervalDialogComponent } from './components/aftale/create/forms/abonnement-form/opkraevnings-interval-dialog/opkraevnings-interval-dialog.component';
import { MatVerticalStepperScrollerDirective } from './common/MatVerticalStepperScrollerDirective';
import { EnvironmentModule } from './environment/environment.module';
import { VersionComponent } from './components/shared/version/version.component';
import { KomponentListComponent } from './components/aftale/details/abonnement/anlaeg/komponent-list/komponent-list.component';
import { TilvalgComponent } from './components/aftale/details/abonnement/tilvalg/tilvalg.component';
import { OpkraevningerComponent } from './components/aftale/details/opkraevninger/opkraevninger.component';
import { PartnerInputComponent } from './components/aftale/create/forms/abonnement-form/partner-input/partner-input.component';
import { PartnerActiveInputComponent } from './components/aftale/create/forms/abonnement-form/partner-input/partner-active-input.component';
import { PartnerFilterComponent } from './components/shared/partner-filter/partner-filter.component';
import { EditPartnerComponent } from './components/aftale/edit-partner/edit-partner.component';
import { FeatureToggleModule } from './feature-toggle/feature-toggle.module';
import { OpfoelgningDialogComponent } from './opfoelgning/components/opfoelgning-dialog/opfoelgning-dialog.component';
import { OpfoelgningActionComponent } from './opfoelgning/component/opfoelgning-action/opfoelgning-action.component';
import { KundeInfoComponent } from './components/shared/kunde-info/kunde-info.component';
import { ServiceintervalerDialogComponent } from './components/aftale/details/serviceintervaller/serviceintervaler-dialog/serviceintervaler-dialog.component';
import { KreditvurderingDialogComponent } from './component/ordre/kreditvurdering-dialog/kreditvurdering-dialog.component';
import { KreditstopDialogComponent } from './component/ordre/kreditstop-dialog/kreditstop-dialog.component';
import { EditOrdreDetailsComponent } from './components/ordre/edit-ordre/edit-ordre-details/edit-ordre-details.component';
import { InfoComponent } from './components/shared/info/info.component';
import { BetalerDialogComponent } from './components/shared/betaler-dialog/betaler-dialog.component';
import { ValidationFailedDialogComponent } from './component/ordre/validation-failed-dialog/validation-failed-dialog.component';
import { RejectPosteringerDialogComponent } from './components/ordre/reject-posteringer-dialog/reject-posteringer-dialog.component';
import { BackButtonComponent } from './components/shared/back-button/back-button.component';
import { AftaleNavComponent } from './components/aftale/aftale-nav/aftale-nav.component';
import { MineSenesteComponent } from './components/aftale/mine-seneste/mine-seneste.component';
import { UnparentedFilterComponent } from './components/ordre/unparented-filter/unparented-filter.component';
import { OpfoelgningServiceordreComponent } from './opfoelgning/components/opfoelgning-serviceordre/opfoelgning-serviceordre.component';
import { OpfoelgningNavComponent } from './opfoelgning/opfoelgning-nav/opfoelgning-nav.component';
import { DateUserComponent } from './components/shared/date-user/date-user.component';
import { OrdreNavComponent } from './components/ordre/ordre-nav/ordre-nav.component';
import { KreditnotaDialogComponent } from './components/kreditnota/kreditnota-dialog/kreditnota-dialog.component';
import { ParkerInfoDialogComponent } from './components/opfoelgning/parker-info-dialog/parker-info-dialog.component';
import { PriceDialogComponent } from './components/aftale/price-dialog/price-dialog.component';
import { OpfoelgningServiceaftaleComponent } from './opfoelgning/components/opfoelgning-serviceaftale/opfoelgning-serviceaftale.component';
import { HistoryFilterComponent } from './components/shared/history/select-filter/select-filter.component';
import { HenviserInputComponent } from './components/aftale/create/forms/abonnement-form/henviser-input/henviser-input.component';
import { SettingsOverviewComponent } from './components/settings/settings-overview/settings-overview.component';
import { HenvisereListComponent } from './components/settings/henvisere/henvisere-list/henvisere-list.component';
import { SettingsNavComponent } from './components/settings/settings-nav/settings-nav.component';
import { HenviserDialogComponent } from './components/settings/henviser/henviser-dialog/henviser-dialog.component';
import { ConfirmCreateDialogComponent } from './components/ordre/confirm-create-dialog/confirm-create-dialog.component';
import { EditServiceabonnementTilvalgDialogComponent } from './components/tilvalg/edit-serviceabonnement-tilvalg-dialog/edit-serviceabonnement-tilvalg-dialog.component';
import { EditTilvalgFormComponent } from './components/tilvalg/edit-tilvalg-form/edit-tilvalg-form.component';
import { ServiceabonnementListComponent } from './components/settings/serviceabonnement-list/serviceabonnement-list.component';
import { ServiceabonnementEditComponent } from './components/settings/serviceabonnement-list/serviceabonnement-edit/serviceabonnement-edit.component';
import { DateFilterDialogComponent } from './components/shared/date-filter-dialog/date-filter-dialog.component';
import { FabrikatEditDialogComponent } from './components/settings/fabrikater/fabrikat-edit-dialog/fabrikat-edit-dialog.component';
import { FabrikatListComponent } from './components/settings/fabrikater/fabrikat-list/fabrikat-list.component';
import { ChangeOrderTypeDialogComponent } from './components/ordre/edit-ordre/change-order-type-dialog/change-order-type-dialog.component';
import { PartnerListComponent } from './components/settings/partner/partner-list/partner-list.component';
import { FastForwardOpkraevningsdateDialogComponent } from './components/aftale/details/abonnement/fast-forward-opkraevningsdate-dialog/fast-forward-opkraevningsdate-dialog.component';
import { PartnerEditDialogComponent } from './components/settings/partner/partner-edit-dialog/partner-edit-dialog.component';
import { PartnerCalculationModelPipe } from './pipes/partner-calculation-model-pipe';
import { PartnerGatewayPipe } from './pipes/partner-gateway-pipe';
import { ScrollToRowDirective } from './directives/scroll-to-row.directive';
import { OpsigFlowComponent } from './components/aftale/details/opsig-flow/opsig-flow.component';
import { OpsigDateComponent } from './components/aftale/details/opsig-flow/opsig-date/opsig-date.component';
import { OpenOrdersCancelComponent } from './components/aftale/details/opsig-flow/open-orders-cancel/open-orders-cancel.component';
import { TerminationReasonComponent } from './components/aftale/details/opsig-flow/termination-reason/termination-reason.component';
import { StartAfregningDialogComponent } from './components/system-status/start-afregning-dialog/start-afregning-dialog.component';
import { InstallatoerListComponent } from './components/settings/installatoer/installatoer-list/installatoer-list.component';
import { InstallatoerDialogComponent } from './components/settings/installatoer/installatoer-dialog/installatoer-dialog.component';
import { InstallatoerInputComponent } from './components/aftale/create/forms/produkt-form/installatoer-input/installatoer-input.component';
import { UpcomingChangesDialogComponent } from './components/aftale/details/upcoming-changes-dialog/upcoming-changes-dialog.component';
import { FastOrdreNoteEditDialogComponent } from './components/aftale/details/fast-ordre-note-edit-dialog/fast-ordre-note-edit-dialog.component';
import { SendLetterComponent } from './components/aftale/details/opsig-flow/send-letter/send-letter.component';
import { OpfoelgningDashboardComponent } from './opfoelgning/components/opfoelgning-dashboard/opfoelgning-dashboard.component';
import { HistoryViewComponent } from './components/shared/history/history-view/history-view.component';
import { FejlDashboardListeComponent } from './components/fejl-dashboard/fejl-dashboard-liste/fejl-dashboard-liste.component';
import { FejlDashboardKnapComponent } from './components/fejl-dashboard/fejl-dashboard-knap/fejl-dashboard-knap.component';
import { FejlDashboardIkonComponent } from './components/fejl-dashboard/fejl-dashboard-ikon/fejl-dashboard-ikon.component';
import { PartnerChangeComponent } from './components/settings/partner/partner-change/partner-change.component';
import { EditMultiplePartnerDialogComponent } from './components/settings/partner/partner-change/edit-multiple-partner-dialog/edit-multiple-partner-dialog.component';
import { TilvalgListComponent } from './components/settings/tilvalg/tilvalg-list/tilvalg-list.component';
import { TilvalgEditDialogComponent } from './components/settings/tilvalg/tilvalg-edit-dialog/tilvalg-edit-dialog.component';
import { ServiceabonnementTilvalgRelationDialogComponent } from './components/settings/serviceabonnement/serviceabonnement-tilvalg-relation-dialog/serviceabonnement-tilvalg-relation-dialog.component';
import { PartnerDialogComponent } from './components/shared/partner-dialog/partner-dialog.component';
import { SlutopgoerelseComponent } from './components/aftale/details/opsig-flow/slutopgoerelse/slutopgoerelse.component';
import { ServiceintervalChangeGraphComponent } from './components/shared/serviceinterval/serviceinterval-change-graph/serviceinterval-change-graph.component';
import { ServiceintervalBarGraphComponent } from './components/shared/serviceinterval/serviceinterval-bar-graph/serviceinterval-bar-graph.component';
import { EditHistoryRemarkDialogComponent } from './components/shared/history/edit-history-remark-dialog/edit-history-remark-dialog.component';
import { ExcelComponent } from './components/shared/excel/excel-component/excel.component';
import { SlutopgoerelseEditorComponent } from './components/slutopgoerelse/slutopgoerelse-editor/slutopgoerelse-editor.component';
import { ProduktCyklusIconComponent } from './components/shared/produkt-cyklus-icon/produkt-cyklus-icon.component';
import { UpcomingChangesComponent } from './components/aftale/details/upcoming-changes/upcoming-changes.component';
import { RecentChangesComponent } from './components/ordre/edit-ordre/recent-changes/recent-changes.component';
import { SlutopgoerelseListComponent } from './components/slutopgoerelse/slutopgoerelse-list/slutopgoerelse-list.component';
import { PartnervarenrListComponent } from './components/settings/partnervarenr/partnervarenr-list/partnervarenr-list.component';
import { PartnervarenrEditDialogComponent } from './components/settings/partnervarenr/partnervarenr-edit-dialog/partnervarenr-edit-dialog.component';
import { CancelOrdreDialogComponent } from './components/ordre/cancel-ordre-dialog/cancel-ordre-dialog.component';
import { SystemStatusOverviewComponent } from './components/system-status/system-status-overview/system-status-overview.component';
import { SystemStatusNavComponent } from './components/system-status/system-status-nav/system-status-nav.component';
import { SystemStatusEftersynComponent } from './components/system-status/system-status-eftersyn/system-status-eftersyn.component';
import { SystemStatusOpkraevningerComponent } from './components/system-status/system-status-opkraevninger/system-status-opkraevninger.component';
import { ExcelDownloadDialogComponent } from './components/shared/excel/excel-download-dialog/excel-download-dialog.component';
import { ServiceordreKildePipe } from './pipes/serviceordre-kilde-pipe';
import { AreaBlacklistComponent } from './components/settings/area-blacklist/area-blacklist.component';
import { AreaBlacklistAddDialogComponent } from './components/settings/area-blacklist/area-blacklist-add-dialog/area-blacklist-add-dialog.component';
import { GarantiaftaleTagComponent } from './components/aftale/garantiaftale/garantiaftale-tag/garantiaftale-tag.component';
import { SlutopgoerelseQuickviewComponent } from './components/aftale/details/opsig-flow/slutopgoerelse-quickview/slutopgoerelse-quickview.component';
import { OpfoelgningButtonComponent } from './components/opfoelgninger/opfoelgning-button/opfoelgning-button.component';
import { EftersynStatusComponent } from './components/aftale/eftersyn-status/eftersyn-status.component';
import { OpkraevningsStatusComponent } from './components/aftale/opkraevnings-status/opkraevnings-status.component';
import { PreventDoubleClickDirective } from './directives/prevent-double-click.directive';
import { RapporterComponent } from './components/ordre/rapporter/rapporter.component';
import { ModelListComponent } from './components/settings/modeller/model-list/model-list.component';
import { ModelEditDialogComponent } from './components/settings/modeller/model-edit-dialog/model-edit-dialog.component';
import { ModelInputComponent } from './components/aftale/create/forms/produkt-form/model-input/model-input.component';
import { SlutopgoerelseDraftComponent } from './components/aftale/details/opsig-flow/slutopgoerelse-draft/slutopgoerelse-draft.component';
import { CopyComponent } from './components/shared/copy/copy.component';
import { ModelListFilterComponent } from './components/setting/modeller/model-list-filter/model-list-filter.component';
import { RabatkampagnerListComponent } from './components/settings/rabatkampagner/rabatkampagner-list/rabatkampagner-list.component';
import { RabatkampagnerEditDialogComponent } from './components/settings/rabatkampagner/rabatkampagner-edit-dialog/rabatkampagner-edit-dialog.component';
import { RabatTypePipe } from './pipes/rabat-type-pipe';
import { FabrikatFilterComponent } from './components/shared/fabrikat-filter/fabrikat-filter.component';
import { RabatKampagneFormComponent } from './components/aftale/create/forms/tilvalg-form/rabat-kampagne-form/rabat-kampagne-form.component';
import { TransferOpfoelgningerDialogComponent } from './components/opfoelgning/transfer-opfoelgninger-dialog/transfer-opfoelgninger-dialog.component';
import { EditAbonnementComponent } from './components/abonnement/edit-abonnement/edit-abonnement.component';
import { EditTilvalgComponent } from './components/tilvalg/edit-tilvalg/edit-tilvalg.component';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { PostnummerListComponent } from './components/settings/partner-postnummer/postnummer-list/postnummer-list.component';
import { PartnerPostnummerEditDialogComponent } from './components/settings/partner-postnummer/partner-postnummer-edit-dialog/partner-postnummer-edit-dialog.component';
import { PartnerListFilterComponent } from './components/setting/partnere/partner-list-filter/partner-list-filter.component';
import { PartnerAbonnementerListComponent } from './components/settings/partnerabonnementer/partnerabonnementer-list/partnerabonnementer-list.component';
import { OAuthModule } from 'angular-oauth2-oidc';
import { ShowPartnerPayloadComponent } from './components/ordre/edit-ordre/partner-payload/partner-payload.component';
import { jsonStringPipe } from './pipes/json-string-pipe';
import { SettlementHaltedDialogComponent } from './component/afregning/settlement-halted-dialog/settlement-halted-dialog.component';
import { SettingsComponent } from './components/settings/settings/settings.component';
import { PartnerabonnementerEditDialogComponent } from './components/settings/partnerabonnementer/partnerabonnementer-edit-dialog/partnerabonnementer-edit-dialog.component';

export function environmentProviderFactory(provider: EnvironmentLoaderService, authService: AuthService) {
  return async () => {
    await provider.load();
    return authService.init();
  };
}
registerLocaleData(localeDA);

@NgModule({
  declarations: [
    AppComponent,
    PageNavFooterComponent,
    PageCleanComponent,
    PageCleanWhiteComponent,
    CreateAftaleComponent,
    CreateTilkaldComponent,
    AftaleListComponent,
    OrdreListComponent,
    QuickviewComponent,
    AftaleDetailsComponent,
    PageNotFoundComponent,
    LoaderComponent,
    KundeFormComponent,
    ProduktFormComponent,
    AbonnementFormComponent,
    TilvalgFormComponent,
    SearchComponent,
    SideNavigationComponent,
    ConfirmDialogComponent,
    AnlaegsKomponentComponent,
    HistoryComponent,
    FabrikatInputComponent,
    ModelInputComponent,
    AbonnementComponent,
    OrdreComponent,
    ForbiddenComponent,
    EditOrdreComponent,
    AnlaegComponent,
    FooterComponent,
    AfregningslinieeditaddComponent,
    dkCurrencyPipe,
    dkPhoneNumberPipe,
    CharacterCountComponent,
    AnlaegEditComponent,
    FastprisAddEditComponent,
    AftaleFilterComponent,
    CreateEftersynComponent,
    OrdreFilterComponent,
    EditTagsComponent,
    UparentedOrdersListComponent,
    UnparentedOrderDetailsComponent,
    PosteringsListComponent,
    RabatAddEditComponent,
    RabatFormComponent,
    EditAbonnementComponent,
    EditTilvalgComponent,
    OpkraevningsIntervalDialogComponent,
    MatVerticalStepperScrollerDirective,
    VersionComponent,
    KomponentListComponent,
    TilvalgComponent,
    OpkraevningerComponent,
    PartnerInputComponent,
    PartnerActiveInputComponent,
    PartnerFilterComponent,
    FabrikatFilterComponent,
    EditPartnerComponent,
    OpfoelgningServiceaftaleComponent,
    OpfoelgningDialogComponent,
    OpfoelgningActionComponent,
    KundeInfoComponent,
    ServiceintervalerDialogComponent,
    KreditvurderingDialogComponent,
    KreditstopDialogComponent,
    EditOrdreDetailsComponent,
    InfoComponent,
    BetalerDialogComponent,
    ValidationFailedDialogComponent,
    RejectPosteringerDialogComponent,
    BackButtonComponent,
    AftaleNavComponent,
    MineSenesteComponent,
    UnparentedFilterComponent,
    HistoryFilterComponent,
    OpfoelgningServiceordreComponent,
    OpfoelgningNavComponent,
    DateUserComponent,
    OrdreNavComponent,
    KreditnotaDialogComponent,
    ParkerInfoDialogComponent,
    PriceDialogComponent,
    HenviserInputComponent,
    SettingsOverviewComponent,
    HenvisereListComponent,
    SettingsNavComponent,
    HenviserDialogComponent,
    ConfirmCreateDialogComponent,
    ServiceabonnementListComponent,
    ServiceabonnementEditComponent,
    EditServiceabonnementTilvalgDialogComponent,
    EditTilvalgFormComponent,
    UpcomingChangesComponent,
    UpcomingChangesDialogComponent,
    DateFilterDialogComponent,
    FabrikatEditDialogComponent,
    FabrikatListComponent,
    ChangeOrderTypeDialogComponent,
    PartnerListFilterComponent,
    PartnerListComponent,
    FastForwardOpkraevningsdateDialogComponent,
    PartnerEditDialogComponent,
    InstallatoerListComponent,
    InstallatoerDialogComponent,
    PartnerGatewayPipe,
    PartnerCalculationModelPipe,
    ScrollToRowDirective,
    InstallatoerInputComponent,
    StartAfregningDialogComponent,
    OpsigFlowComponent,
    OpsigDateComponent,
    OpenOrdersCancelComponent,
    TerminationReasonComponent,
    FastOrdreNoteEditDialogComponent,
    SendLetterComponent,
    OpfoelgningDashboardComponent,
    HistoryViewComponent,
    FejlDashboardListeComponent,
    FejlDashboardKnapComponent,
    FejlDashboardIkonComponent,
    TilvalgListComponent,
    TilvalgEditDialogComponent,
    ServiceabonnementTilvalgRelationDialogComponent,
    PartnerChangeComponent,
    EditMultiplePartnerDialogComponent,
    PartnerDialogComponent,
    SlutopgoerelseComponent,
    SlutopgoerelseDraftComponent,
    ServiceintervalChangeGraphComponent,
    ServiceintervalBarGraphComponent,
    ExcelComponent,
    EditHistoryRemarkDialogComponent,
    ProduktCyklusIconComponent,
    RecentChangesComponent,
    SlutopgoerelseEditorComponent,
    SlutopgoerelseListComponent,
    PartnervarenrListComponent,
    PartnervarenrEditDialogComponent,
    CancelOrdreDialogComponent,
    SystemStatusOverviewComponent,
    SystemStatusNavComponent,
    SystemStatusEftersynComponent,
    SystemStatusOpkraevningerComponent,
    ExcelDownloadDialogComponent,
    ServiceordreKildePipe,
    AreaBlacklistComponent,
    AreaBlacklistAddDialogComponent,
    GarantiaftaleTagComponent,
    SlutopgoerelseQuickviewComponent,
    OpfoelgningButtonComponent,
    EftersynStatusComponent,
    OpkraevningsStatusComponent,
    PreventDoubleClickDirective,
    RapporterComponent,
    ModelListComponent,
    ModelEditDialogComponent,
    CopyComponent,
    RabatkampagnerListComponent,
    RabatkampagnerEditDialogComponent,
    RabatTypePipe,
    ModelListFilterComponent,
    RabatKampagneFormComponent,
    TransferOpfoelgningerDialogComponent,
    PostnummerListComponent,
    PartnerPostnummerEditDialogComponent,
    PartnerAbonnementerListComponent,
    ShowPartnerPayloadComponent,
    jsonStringPipe,
    SettlementHaltedDialogComponent,
    SettingsComponent,
    PartnerabonnementerEditDialogComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    HttpClientModule,
    MaterialModule,
    FormsModule,
    MomentModule,
    ApiModule,
    NotificationsModule,
    EnvironmentModule,
    FeatureToggleModule,
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true,
      },
    }),
  ],
  providers: [
    CustomIconService,
    dkCurrencyPipe,
    dkPhoneNumberPipe,
    DatePipe,
    jsonStringPipe,
    { provide: APP_INITIALIZER, useFactory: environmentProviderFactory, deps: [EnvironmentLoaderService, AuthService], multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CredentialsInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: H401Interceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenInterceptor,
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
    { provide: LOCALE_ID, useValue: 'da-DK' },
    { provide: DateAdapter, useClass: DkDateAdapter },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
